'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Box, CircularProgress, Typography } from '@/primitives';
import { useMatingPredictor } from '@/hooks/publicSearch/mating/useMatingPredictor';
import MatingPredictorSearch, {
  defaultFormValues,
} from './MatingPredictorSearch';
import MatingPredictorResults from './MatingPredictorResults';
import MatingPedigreeDisplay from './MatingPedigreeDisplay';
import type {
  MatingDisplayBy,
  MatingSearchFormValues,
  PedigreeDisplayState,
} from './types';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useMatingPredictorStore } from '@/publicSearch/store/useMatingPredictorStore';
import {
  MATING_VIEW_PARAM,
  MATING_SIRE_ID_PARAM,
  MATING_DAM_ID_PARAM,
  MATING_SIRES_PARAM,
  MATING_DAMS_PARAM,
  MATING_CALVING_YEARS_PARAM,
  MATING_MAX_ANIMALS_PARAM,
} from './matingResultsUrls';

function sameMatingSearchCriteria(
  first: MatingSearchFormValues,
  second: MatingSearchFormValues,
): boolean {
  return (
    first.sireIdents === second.sireIdents &&
    first.damIdents === second.damIdents &&
    first.damCalvingYears === second.damCalvingYears &&
    first.maxAnimals === second.maxAnimals
  );
}

export default function MatingPredictorPage() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const orgId = usePublicSearchContext((context) => context.society.orgId);

  const formValues = useMatingPredictorStore((state) => state.formValues);
  const setFormValues = useMatingPredictorStore((state) => state.setFormValues);
  const displayBy = useMatingPredictorStore((state) => state.displayBy);
  const setDisplayBy = useMatingPredictorStore((state) => state.setDisplayBy);
  const data = useMatingPredictorStore((state) => state.data);
  const setData = useMatingPredictorStore((state) => state.setData);
  const clear = useMatingPredictorStore((state) => state.clear);

  const { calculate, loading, error } = useMatingPredictor();

  const pendingAutoSearchRef = useRef<MatingSearchFormValues | null>(null);

  useEffect(() => {
    if (orgId == null || orgId.trim() === '') return;
    const { data: storedData, persistedOrgId } =
      useMatingPredictorStore.getState();
    if (
      storedData &&
      persistedOrgId != null &&
      persistedOrgId.trim() !== '' &&
      persistedOrgId !== orgId
    ) {
      clear();
    }
  }, [orgId, clear]);

  // Hydrate form values from URL on mount (for shared links)
  useEffect(() => {
    const siresParam = searchParams.get(MATING_SIRES_PARAM);
    const damsParam = searchParams.get(MATING_DAMS_PARAM);
    if (!siresParam || !damsParam) return;

    const maxAnimalsStr = searchParams.get(MATING_MAX_ANIMALS_PARAM);
    const maxAnimals =
      maxAnimalsStr != null && !Number.isNaN(Number(maxAnimalsStr))
        ? Number(maxAnimalsStr)
        : defaultFormValues.maxAnimals;

    const storeState = useMatingPredictorStore.getState();
    const urlFormValues: MatingSearchFormValues = {
      sireIdents: siresParam,
      damIdents: damsParam,
      damCalvingYears: searchParams.get(MATING_CALVING_YEARS_PARAM) ?? '',
      maxAnimals,
      displayBy: storeState.displayBy,
    };
    setFormValues(urlFormValues);

    const urlDoesNotMatchStoredSearch = !sameMatingSearchCriteria(
      storeState.formValues,
      urlFormValues,
    );
    if (!storeState.data || urlDoesNotMatchStoredSearch) {
      if (storeState.data && urlDoesNotMatchStoredSearch) {
        setData(null, orgId ?? storeState.persistedOrgId ?? '');
      }
      pendingAutoSearchRef.current = urlFormValues;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search when orgId is ready after URL hydration
  useEffect(() => {
    if (!pendingAutoSearchRef.current) return;
    if (!orgId || orgId.trim() === '') return;

    const pendingSearchValues = pendingAutoSearchRef.current;
    pendingAutoSearchRef.current = null;
    setFormValues(pendingSearchValues); // Re-apply in case clear() ran before orgId resolved
    void calculate(pendingSearchValues)
      .then((result) => {
        setData(result, orgId);
      })
      .catch(() => {});
  }, [orgId, calculate, setData, setFormValues]);

  const isPedigreeView = useMemo(() => {
    return searchParams.get(MATING_VIEW_PARAM) === 'pedigree';
  }, [searchParams]);

  const pedigreeStateFromUrl = useMemo((): PedigreeDisplayState | null => {
    if (!isPedigreeView || !data) return null;
    const sireIdParam = searchParams.get(MATING_SIRE_ID_PARAM);
    const damIdParam = searchParams.get(MATING_DAM_ID_PARAM);
    if (!sireIdParam || !damIdParam) return null;
    const sireId = Number(sireIdParam);
    const damId = Number(damIdParam);
    if (Number.isNaN(sireId) || Number.isNaN(damId)) return null;
    const sire = data.sires.find(
      (sireOption) => sireOption.animalId === sireId,
    );
    const dam = data.dams.find((damOption) => damOption.animalId === damId);
    if (!sire || !dam) return null;
    return {
      sireId,
      damId,
      sireIdent: sire.animalIdent ?? '',
      damIdent: dam.animalIdent ?? '',
      sireName: sire.animalName ?? '',
      damName: dam.animalName ?? '',
    };
  }, [isPedigreeView, data, searchParams]);

  const updateSearchUrl = useCallback(
    (searchValues: MatingSearchFormValues) => {
      const queryParams = new URLSearchParams(searchParams.toString());
      queryParams.set(MATING_SIRES_PARAM, searchValues.sireIdents);
      queryParams.set(MATING_DAMS_PARAM, searchValues.damIdents);
      if (searchValues.damCalvingYears) {
        queryParams.set(
          MATING_CALVING_YEARS_PARAM,
          searchValues.damCalvingYears,
        );
      } else {
        queryParams.delete(MATING_CALVING_YEARS_PARAM);
      }
      if (searchValues.maxAnimals !== defaultFormValues.maxAnimals) {
        queryParams.set(
          MATING_MAX_ANIMALS_PARAM,
          String(searchValues.maxAnimals),
        );
      } else {
        queryParams.delete(MATING_MAX_ANIMALS_PARAM);
      }
      queryParams.delete(MATING_VIEW_PARAM);
      queryParams.delete(MATING_SIRE_ID_PARAM);
      queryParams.delete(MATING_DAM_ID_PARAM);
      const query = queryParams.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    },
    [pathname, router, searchParams],
  );

  const clearSearchUrl = useCallback(() => {
    const queryParams = new URLSearchParams(searchParams.toString());
    queryParams.delete(MATING_SIRES_PARAM);
    queryParams.delete(MATING_DAMS_PARAM);
    queryParams.delete(MATING_CALVING_YEARS_PARAM);
    queryParams.delete(MATING_MAX_ANIMALS_PARAM);
    queryParams.delete(MATING_VIEW_PARAM);
    queryParams.delete(MATING_SIRE_ID_PARAM);
    queryParams.delete(MATING_DAM_ID_PARAM);
    const query = queryParams.toString();
    router.replace(query ? `${pathname}?${query}` : pathname);
  }, [pathname, router, searchParams]);

  const handleSearch = useCallback(async () => {
    try {
      const result = await calculate(formValues);
      setData(result, orgId ?? '');
      updateSearchUrl(formValues);
    } catch {
      // Error is captured in the hook's error state
    }
  }, [calculate, formValues, orgId, setData, updateSearchUrl]);

  const handleClear = useCallback(() => {
    clear();
    clearSearchUrl();
  }, [clear, clearSearchUrl]);

  const handleDisplayByChange = useCallback(
    (value: MatingDisplayBy) => {
      setDisplayBy(value);
      const currentFormValues = useMatingPredictorStore.getState().formValues;
      setFormValues({ ...currentFormValues, displayBy: value });
    },
    [setDisplayBy, setFormValues],
  );

  if (isPedigreeView && pedigreeStateFromUrl && data) {
    return <MatingPedigreeDisplay pedigreeState={pedigreeStateFromUrl} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        pt: { xs: 1, md: 1.5 },
      }}
    >
      {error && (
        <Typography variant="bodymedium" color="error" sx={{ mb: 2 }}>
          {error.message}
        </Typography>
      )}
      <MatingPredictorSearch
        formValues={formValues}
        onFormChange={setFormValues}
        onSearch={handleSearch}
        onClear={handleClear}
        loading={loading}
      />
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      {!loading && data && (
        <MatingPredictorResults
          data={data}
          displayBy={displayBy}
          onDisplayByChange={handleDisplayByChange}
        />
      )}
      {!loading && !data && (
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <Image
            src="/empty-inbox.svg"
            alt="No results"
            width={70}
            height={100}
          />
          <Typography
            variant="labellarge"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              paddingBlock: 2,
            }}
          >
            Enter sire and dam identifiers above and click Search to calculate
            mating predictions.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
